import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess } from '@/types/api';
import type { TimetablePeriod } from '@/types/timetable';
import type {
  CopyTimetableResult,
  CreateEntryPayload,
  CreateTimetablePayload,
  PeriodPayload,
  SectionTimetables,
  SectionWeek,
  TimetableDatesPayload,
  TimetableVersion,
  UpdateEntryPayload,
} from '@/types/timetableAdmin';

/**
 * Every call the office's timetable builder makes.
 *
 * Separate from the teacher's `features/teacher/api/timetable.api.ts` because
 * they are different jobs against different endpoints: the teacher reads their
 * own week and marks availability, the office builds the week for everybody.
 * One file holding both would be one import that drags the whole thing into
 * either bundle.
 */
export const instituteTimetableApi = {
  /** The day's shape for the current year. */
  periods: () => unwrap(apiClient.get<ApiSuccess<TimetablePeriod[]>>('/institute/timetable/periods')),

  createPeriod: (payload: PeriodPayload) =>
    unwrap(apiClient.post<ApiSuccess<TimetablePeriod>>('/institute/timetable/periods', payload)),

  updatePeriod: (uuid: string, payload: PeriodPayload) =>
    unwrap(
      apiClient.patch<ApiSuccess<TimetablePeriod>>(
        `/institute/timetable/periods/${uuid}`,
        payload,
      ),
    ),

  /** Refused while any section still has a lesson in it. */
  removePeriod: (uuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<null>>(`/institute/timetable/periods/${uuid}`)),

  /**
   * One section's recurring week in one of its timetables, with who teaches
   * each slot. Without a timetable, the server picks the one in force today.
   */
  sectionWeek: (classSectionUuid: string, timetableUuid?: string) =>
    unwrap(
      apiClient.get<ApiSuccess<SectionWeek>>(`/institute/timetable/sections/${classSectionUuid}`, {
        params: timetableUuid ? { timetable: timetableUuid } : undefined,
      }),
    ),

  /** A section's dated timetables, earliest first. */
  timetables: (classSectionUuid: string) =>
    unwrap(
      apiClient.get<ApiSuccess<SectionTimetables>>(
        `/institute/timetable/sections/${classSectionUuid}/timetables`,
      ),
    ),

  /** Refused when the dates overlap another of the section's timetables. */
  createTimetable: (classSectionUuid: string, payload: CreateTimetablePayload) =>
    unwrap(
      apiClient.post<ApiSuccess<TimetableVersion>>(
        `/institute/timetable/sections/${classSectionUuid}/timetables`,
        payload,
      ),
    ),

  updateTimetableDates: (uuid: string, payload: TimetableDatesPayload) =>
    unwrap(
      apiClient.patch<ApiSuccess<TimetableVersion>>(
        `/institute/timetable/timetables/${uuid}`,
        payload,
      ),
    ),

  /** Gives a draft to the section's teachers and families. Already published: no change. */
  publishTimetable: (uuid: string) =>
    unwrap(
      apiClient.post<ApiSuccess<TimetableVersion>>(`/institute/timetable/timetables/${uuid}/publish`),
    ),

  /** Takes the timetable away with every lesson in it. */
  removeTimetable: (uuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<null>>(`/institute/timetable/timetables/${uuid}`)),

  createEntry: (payload: CreateEntryPayload) =>
    unwrap(
      apiClient.post<ApiSuccess<SectionWeekEntryResponse>>('/institute/timetable/entries', payload),
    ),

  /**
   * Corrects a placed lesson — dragged to another cell, or reassigned to the
   * subject it should have been. The section is never part of it.
   */
  updateEntry: (uuid: string, payload: UpdateEntryPayload) =>
    unwrap(
      apiClient.patch<ApiSuccess<SectionWeekEntryResponse>>(
        `/institute/timetable/entries/${uuid}`,
        payload,
      ),
    ),

  removeEntry: (uuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<null>>(`/institute/timetable/entries/${uuid}`)),

  /**
   * Copies a timetable, dates and lessons, into other sections. Lessons a
   * section cannot take come back as skipped, with the reason.
   */
  copyTimetable: (uuid: string, targetSectionUuids: string[]) =>
    unwrap(
      apiClient.post<ApiSuccess<CopyTimetableResult>>(
        `/institute/timetable/timetables/${uuid}/copy`,
        { targetSectionUuids },
      ),
    ),
};

/** What `createEntry` echoes back — the same shape the grid already renders. */
type SectionWeekEntryResponse = SectionWeek['entries'][number];
